import type React from "react";
import { theme, card } from "../theme";
import { RulerProgress, StatusTag } from "./Drawing";
import type { ExecutiveReportMetrics, RiskSeverity } from "../types/executiveReport";
import { describeControlIds, displayControlRef } from "../lib/controlRef";

import { cbPalette } from "../lib/colourBlind";
/** "1 control", "3 controls" — never "control(s)". */
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// The executive report's dashboard on screen — the same blocks the stored PDF
// and Word versions carry (services/executiveReportPdf.ts / executiveReportDocx.ts
// on the API), drawn from the report's stored metrics snapshot.

const RAG = cbPalette({ compliant: "#22c55e", partial: "#f59e0b", gap: "#ef4444", none: "var(--c-border)" });
const SEVERITY: Record<RiskSeverity, { color: string; bg: string; label: string }> = {
  critical: { color: theme.color.riskCritText, bg: theme.color.riskCritBg, label: "Critical" },
  high:     { color: theme.color.riskHighText, bg: theme.color.riskHighBg, label: "High" },
  medium:   { color: theme.color.riskMedText,  bg: theme.color.riskMedBg,  label: "Medium" },
  low:      { color: theme.color.riskLowText,  bg: theme.color.riskLowBg,  label: "Low" },
};
const scoreColor = (s: number) => (s >= 80 ? RAG.compliant : s >= 50 ? RAG.partial : RAG.gap);
const fmtShort = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "n/a");
// Asset inventory bar: one colour per discovery source (lib/assetInventoryMetrics.ts ASSET_SOURCE_COLORS on the API) and grey for rows entered by members.
const SOURCE_COLORS: Record<string, string> = cbPalette({
  intune: "#2563eb", defender: "#1d4ed8", m365: "#0ea5e9", google: "#10b981", aws: "#f97316", gcp: "#db2777", firewall: "#7c3aed",
  sentinel: "#4f46e5", storage: "#14b8a6", supplier: "#f59e0b", ropa: "#84cc16", site: "#92400e", manual: "#9ca3af",
});
const sourceColor = (source: string) => SOURCE_COLORS[source] ?? SOURCE_COLORS.manual;

const sectionTitle: React.CSSProperties = { fontSize: 14, fontWeight: 700, color: theme.color.heading, marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${theme.color.border}` };
const subTitle: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: theme.color.subtle, margin: "14px 0 8px", textTransform: "uppercase", letterSpacing: "0.04em" };
const th: React.CSSProperties = { padding: "7px 10px", textAlign: "left", fontWeight: 700, color: theme.color.heading, background: theme.color.panelAlt, borderBottom: "1px solid var(--c-line)", whiteSpace: "nowrap", fontSize: 12 };
const td: React.CSSProperties = { padding: "7px 10px", color: theme.color.text, verticalAlign: "top", fontSize: 12, borderBottom: `1px solid ${theme.color.border}` };

export function DonutChart({ score, compliant, partial, gap, total, size = 88, centre, centreColor, caption = "score" }: {
  score: number; compliant: number; partial: number; gap: number; total: number; size?: number;
  /** Replaces the "NN%" figure in the middle, e.g. "Exempt" for a law with nothing applicable. */
  centre?: string; centreColor?: string; caption?: string;
}) {
  const r = 38;
  const circ = 2 * Math.PI * r;
  const safeTotal = total > 0 ? total : 1;
  const compliantLen = (compliant / safeTotal) * circ;
  const partialLen = (partial / safeTotal) * circ;
  const gapLen = (gap / safeTotal) * circ;
  const centreText = centre ?? `${score}%`;
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label={centre ? `${centre} ${caption}` : `Score ${score}%`}>
      <g transform="rotate(-90 50 50)">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--c-border)" strokeWidth="12" />
        {gapLen > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke={RAG.gap} strokeWidth="12" strokeDasharray={`${gapLen} ${circ}`} strokeDashoffset={-(compliantLen + partialLen)} />}
        {partialLen > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke={RAG.partial} strokeWidth="12" strokeDasharray={`${partialLen} ${circ}`} strokeDashoffset={-compliantLen} />}
        {compliantLen > 0 && <circle cx="50" cy="50" r={r} fill="none" stroke={RAG.compliant} strokeWidth="12" strokeDasharray={`${compliantLen} ${circ}`} strokeDashoffset={0} />}
      </g>
      <text x="50" y="47" textAnchor="middle" fontSize={centreText.length > 4 ? 13 : 20} fontWeight="800" fill={centreColor ?? scoreColor(score)} fontFamily={theme.font}>{centreText}</text>
      <text x="50" y="62" textAnchor="middle" fontSize="9" fill="var(--c-subtle)" fontFamily={theme.font}>{caption}</text>
    </svg>
  );
}

// One ring per standard or per law, laid out as a grid with a legend — the
// per-item view of a section; the stored PDF and Word versions carry the same
// figures as bar rows.
interface RingItem { key: string; label: string; sub: string; score: number; compliant: number; partial: number; gap: number; total: number; centre?: string; caption?: string }

function RingGrid({ items, legend }: { items: RingItem[]; legend: { color: string; label: string }[] }) {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
        {items.map((it) => (
          <div key={it.key} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 8px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, opacity: it.centre ? 0.7 : 1 }}>
            <DonutChart score={it.score} compliant={it.compliant} partial={it.partial} gap={it.gap} total={it.total} size={80}
              centre={it.centre} centreColor={it.centre ? theme.color.muted : undefined} caption={it.caption} />
            <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.heading, textAlign: "center", lineHeight: 1.3 }}>{it.label}</div>
            <div style={{ fontSize: 10, color: theme.color.subtle, textAlign: "center" }}>{it.sub}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 14, marginTop: 12, justifyContent: "center", flexWrap: "wrap" }}>
        {legend.map(({ color, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: theme.color.subtle }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />{label}
          </div>
        ))}
      </div>
    </>
  );
}

function StackedBar({ segments, total, height = 10 }: { segments: { value: number; color: string }[]; total: number; height?: number }) {
  return (
    <div style={{ height, background: theme.color.border, borderRadius: height / 2, overflow: "hidden", display: "flex" }}>
      {total > 0 && segments.map((s, i) => (
        <div key={i} style={{ width: `${(Math.max(0, s.value) / total) * 100}%`, background: s.color, height: "100%", transition: "width 0.3s" }} />
      ))}
    </div>
  );
}

function BarRow({ label, sub, segments, total, value, ruler }: { label: string; sub: string; segments: { value: number; color: string }[]; total: number; value: string; ruler?: boolean }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.2fr) 2fr 52px", gap: 12, alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${theme.color.border}` }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={label}>{label}</div>
        <div style={{ fontSize: 10.5, color: theme.color.subtle }}>{sub}</div>
      </div>
      {ruler ? <RulerProgress value={segments[0]?.value ?? 0} max={total} label={label} /> : <StackedBar segments={segments} total={total} />}
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, textAlign: "right" }}>{value}</div>
    </div>
  );
}

function Tile({ value, caption, sub, color, children }: { value: string; caption: string; sub?: string; color?: string; children?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: theme.radius.md, minWidth: 0 }}>
      {children}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: color ?? theme.color.heading, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 10, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 3 }}>{caption}</div>
        {sub && <div style={{ fontSize: 11, color: theme.color.text, marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function CountTiles({ counts, labelPrefix }: { counts: Record<RiskSeverity, number>; labelPrefix?: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
      {(["critical", "high", "medium", "low"] as const).map((s) => (
        <div key={s} style={{ padding: "10px 12px", borderRadius: 3, background: counts[s] > 0 ? SEVERITY[s].bg : theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderLeft: `4px solid ${counts[s] > 0 ? SEVERITY[s].color : theme.color.border}` }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: counts[s] > 0 ? SEVERITY[s].color : theme.color.faint, lineHeight: 1 }}>{counts[s]}</div>
          <div style={{ fontSize: 10, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 4 }}>{labelPrefix}{SEVERITY[s].label}</div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ points }: { points: { date: string; score: number }[] }) {
  const W = 640, H = 150, PL = 34, PR = 10, PT = 10, PB = 22;
  const scores = points.map((p) => p.score);
  const lo = Math.max(0, Math.floor((Math.min(...scores) - 8) / 10) * 10);
  const hi = Math.min(100, Math.ceil((Math.max(...scores) + 8) / 10) * 10);
  const range = hi - lo || 1;
  const step = range <= 40 ? 10 : 20;
  const x = (i: number) => PL + (points.length === 1 ? (W - PL - PR) / 2 : (i / (points.length - 1)) * (W - PL - PR));
  const y = (s: number) => PT + (1 - (s - lo) / range) * (H - PT - PB);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.score).toFixed(1)}`).join(" ");
  const area = `${path} L${x(points.length - 1).toFixed(1)},${y(lo).toFixed(1)} L${x(0).toFixed(1)},${y(lo).toFixed(1)} Z`;
  const ticks: number[] = [];
  for (let v = lo; v <= hi; v += step) ticks.push(v);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" style={{ display: "block" }} role="img" aria-label="Compliance score trend">
      {ticks.map((v) => (
        <g key={v}>
          <line x1={PL} x2={W - PR} y1={y(v)} y2={y(v)} stroke="var(--c-border)" strokeWidth="1" />
          <text x={PL - 6} y={y(v) + 3} textAnchor="end" fontSize="9" fill="var(--c-subtle)" fontFamily={theme.font}>{v}%</text>
        </g>
      ))}
      {points.length > 1 && <path d={area} fill="var(--c-accent)" opacity="0.12" />}
      {points.length > 1 && <path d={path} fill="none" stroke="var(--c-accent)" strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />}
      {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.score)} r="3.2" fill="var(--c-accent)" stroke="var(--c-panel)" strokeWidth="1.5"><title>{`${fmtShort(p.date)}: ${p.score}%`}</title></circle>)}
      <text x={PL} y={H - 6} fontSize="9" fill="var(--c-subtle)" fontFamily={theme.font}>{fmtShort(points[0].date)}</text>
      {points.length > 1 && <text x={W - PR} y={H - 6} textAnchor="end" fontSize="9" fill="var(--c-subtle)" fontFamily={theme.font}>{fmtShort(points[points.length - 1].date)}</text>}
    </svg>
  );
}

export function ExecutiveReportDashboard({ m }: { m: ExecutiveReportMetrics }) {
  const overall = m.overall ? m.overall.score : m.checkRun?.overallScore ?? null;
  // Reports written since the unified score count what the sidebar counts: met over applicable, work in progress earns nothing.
  const soa = m.scoreBasis === "soa";
  const midLabel = soa ? "In progress" : "Partial";
  const change = m.trend.change;
  const risksHigh = m.risks.bySeverity.critical + m.risks.bySeverity.high;
  const changeColor = change == null ? theme.color.muted : change > 0 ? RAG.compliant : change < 0 ? RAG.gap : theme.color.heading;
  const overdueSupplier = (iso: string | null) => !!iso && new Date(iso) < new Date(m.generatedAt);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {m.scope && (
        <div role="note" style={{ ...card, fontSize: 12, color: theme.color.text }}>
          <strong>Limited to the standards your role covers:</strong> {m.scope.labels.join(", ")}. Other standards are not included in this report.
        </div>
      )}
      {/* Headline */}
      <div style={card}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginBottom: 14 }}>
          <Tile value={overall == null ? "n/a" : `${overall}%`} caption="Overall score" sub={soa ? `${m.controls.compliant} of ${m.controls.total} applicable controls met` : `${m.controls.compliant} met, ${m.controls.partial} partly met (counted as half) of ${m.controls.total} controls`} color={overall == null ? theme.color.muted : scoreColor(overall)}>
            <DonutChart score={overall ?? 0} compliant={m.controls.compliant} partial={m.controls.partial} gap={m.controls.gap} total={m.controls.total} size={56} />
          </Tile>
          <Tile value={change == null ? "—" : `${change > 0 ? "+" : ""}${change}`} caption="Since last check" color={changeColor}
            sub={m.trend.previous ? `from ${m.trend.previous.score ?? "n/a"}%, ${fmtShort(m.trend.previous.completedAt)}` : "First completed check"} />
          <Tile value={m.legal ? `${m.legal.pct}%` : "n/a"} caption="Legal obligations" color={m.legal ? scoreColor(m.legal.pct) : theme.color.muted}
            sub={m.legal ? `${m.legal.compliant}/${m.legal.total} across ${m.legal.regulations.length} law${m.legal.regulations.length === 1 ? "" : "s"}` : "No laws mapped yet"} />
          <Tile value={String(m.risks.open)} caption="Open risks" color={risksHigh > 0 ? SEVERITY.high.color : theme.color.heading}
            sub={`${m.risks.bySeverity.critical} critical · ${m.risks.bySeverity.high} high`} />
        </div>
        <StackedBar segments={[{ value: m.controls.compliant, color: RAG.compliant }, { value: m.controls.partial, color: RAG.partial }, { value: m.controls.gap, color: RAG.gap }]} total={m.controls.total} />
        <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
          {[["Compliant", m.controls.compliant, RAG.compliant], [midLabel, m.controls.partial, RAG.partial], ["Gap", m.controls.gap, RAG.gap], ["Not applicable", m.controls.notApplicable, RAG.none]].map(([label, val, color]) => (
            <div key={String(label)} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: theme.color.subtle }}>
              <span style={{ width: 9, height: 9, borderRadius: "50%", background: String(color) }} />{label}: <strong style={{ color: theme.color.text }}>{val}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* Standards + trend */}
      <div style={card}>
        <div style={sectionTitle}>Standards overview</div>
        {m.frameworkScores.length === 0 && <div style={{ fontSize: 12, color: theme.color.subtle }}>No standards enrolled yet.</div>}
        {m.frameworkScores.length > 0 && (
          <RingGrid
            items={m.frameworkScores.map((f) => ({
              key: f.frameworkId, label: f.label, score: f.score ?? 0, compliant: f.compliant, partial: f.partial, gap: f.gap, total: f.total,
              sub: f.total ? `${f.compliant}/${f.total} met · ${f.partial} ${midLabel.toLowerCase()} · ${f.gap} gap` : soa ? "No scoreable controls" : "Not assessed in the latest check",
              centre: f.total && f.score != null ? undefined : "n/a", caption: f.total && f.score != null ? undefined : "not assessed",
            }))}
            legend={[{ color: RAG.compliant, label: "Compliant" }, { color: RAG.partial, label: midLabel }, { color: RAG.gap, label: "Gap" }]}
          />
        )}
        <div style={subTitle}>Compliance trend</div>
        {m.trend.points.length >= 2 ? (
          <>
            <TrendChart points={m.trend.points} />
            <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6 }}>
              {m.trend.points.length} daily snapshots · {m.trend.points[0].score}% on {fmtShort(m.trend.points[0].date)} to {m.trend.points[m.trend.points.length - 1].score}% on {fmtShort(m.trend.points[m.trend.points.length - 1].date)}
            </div>
          </>
        ) : (
          <div style={{ fontSize: 12, color: theme.color.subtle }}>Trend data is still being collected — a daily snapshot is recorded after each completed check; the chart appears once two or more days of history exist.</div>
        )}
        {(m.trend.newlyCompliant.length > 0 || m.trend.regressed.length > 0) && (
          <div style={{ fontSize: 12, color: theme.color.text, marginTop: 8 }}>
            <strong>Since the previous check:</strong> {plural(m.trend.newlyCompliant.length, "control")} became compliant
            {m.trend.newlyCompliant.length > 0 && <span style={{ color: theme.color.subtle }}> ({describeControlIds(m.trend.newlyCompliant)})</span>}
            ; {m.trend.regressed.length} regressed
            {m.trend.regressed.length > 0 && <strong style={{ color: theme.color.dangerText }}> ({describeControlIds(m.trend.regressed)})</strong>}.
          </div>
        )}
      </div>

      {/* Legal */}
      {m.legal && (() => {
        const tracked = m.legal.byRegulation.filter((r) => r.total > 0);
        const exempt = m.legal.byRegulation.filter((r) => r.total === 0 && r.notApplicable > 0);
        const untracked = m.legal.byRegulation.filter((r) => r.total === 0 && r.notApplicable === 0);
        return (
          <div style={card}>
            <div style={sectionTitle}>Legal and regulatory obligations</div>
            <div style={{ fontSize: 11, color: theme.color.subtle, marginBottom: 12 }}>
              {m.legal.regulations.length} law{m.legal.regulations.length === 1 ? "" : "s"} in scope · {m.legal.total} applicable obligation{m.legal.total === 1 ? "" : "s"} · <strong style={{ color: scoreColor(m.legal.pct) }}>{m.legal.pct}%</strong> met overall
            </div>
            {(tracked.length > 0 || exempt.length > 0) && (
              <RingGrid
                items={[
                  ...tracked.map((r) => ({
                    key: r.regulation, label: r.regulation, score: r.pct, compliant: r.compliant, partial: r.inProgress, gap: r.gap, total: r.total,
                    sub: `${r.compliant}/${r.total} met${r.notApplicable > 0 ? ` · ${r.notApplicable} exempt` : ""}`,
                  })),
                  ...exempt.map((r) => ({
                    key: r.regulation, label: r.regulation, score: 0, compliant: 0, partial: 0, gap: 0, total: 0,
                    sub: `${r.notApplicable} obligation${r.notApplicable === 1 ? "" : "s"} exempt`, centre: "Exempt", caption: "by threshold",
                  })),
                ]}
                legend={[{ color: RAG.compliant, label: "Compliant" }, { color: RAG.partial, label: "In progress" }, { color: RAG.gap, label: "Gap" }, { color: RAG.none, label: "Not started" }]}
              />
            )}
            {(m.legal.notApplicable > 0 || untracked.length > 0) && (
              <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 8 }}>
                {m.legal.notApplicable > 0 && <>{m.legal.notApplicable === 1 ? "1 obligation is" : `${m.legal.notApplicable} obligations are`} exempt under a statutory threshold or marked not applicable and are excluded from the figures above. </>}
                {untracked.length > 0 && <><strong style={{ color: theme.color.text }}>Also in scope, no separate obligations tracked:</strong> {untracked.map((r) => r.regulation).join(", ")}.</>}
              </div>
            )}
          </div>
        );
      })()}

      {/* Risks and suppliers */}
      <div style={card}>
        <div style={sectionTitle}>Risk and third-party exposure</div>
        <div style={{ ...subTitle, marginTop: 0 }}>Open risks by residual severity</div>
        <CountTiles counts={m.risks.bySeverity} />
        <div style={{ fontSize: 11, color: theme.color.subtle, margin: "8px 0 4px" }}>
          <strong style={{ color: theme.color.text }}>{m.risks.open} open of {m.risks.total} risks</strong> · {m.risks.bySeverity.unscored} unscored · {plural(m.risks.overdueReviews, "overdue review")} · {plural(m.risks.treatmentsOutstanding, "treatment")} outstanding ({m.risks.treatmentsOverdue} overdue)
        </div>
        {m.risks.top.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Risk</th><th style={th}>Category</th><th style={th}>Owner</th><th style={{ ...th, textAlign: "center" }}>Residual</th><th style={th}>Status</th></tr></thead>
              <tbody>
                {m.risks.top.map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.displayId != null && <span style={{ color: theme.color.subtle, fontVariantNumeric: "tabular-nums", marginRight: 6 }}>RISK-{String(r.displayId).padStart(3, "0")}</span>}{r.title}</td>
                    <td style={td}>{r.category}</td>
                    <td style={td}>{r.owner ?? "—"}</td>
                    <td style={{ ...td, textAlign: "center", fontWeight: 700, color: r.severity ? SEVERITY[r.severity].color : theme.color.faint }}>{r.residualScore ?? "—"}{r.severity ? ` ${SEVERITY[r.severity].label}` : ""}</td>
                    <td style={td}>{r.status.replace(/_/g, " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={subTitle}>Suppliers by risk tier</div>
        <CountTiles counts={m.suppliers.byTier} />
        <div style={{ fontSize: 11, color: theme.color.subtle, margin: "8px 0 4px" }}>
          <strong style={{ color: theme.color.text }}>{m.suppliers.active} active suppliers</strong> · {plural(m.suppliers.dataProcessors, "data processor")} · {plural(m.suppliers.overdueReviews, "overdue review")} · {m.suppliers.neverReviewed} never reviewed
        </div>
        {m.suppliers.highRisk.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>High-risk supplier</th><th style={th}>Tier</th><th style={{ ...th, textAlign: "center" }}>Score</th><th style={th}>Next review</th></tr></thead>
              <tbody>
                {m.suppliers.highRisk.map((s) => (
                  <tr key={s.name}>
                    <td style={td}>{s.name}</td>
                    <td style={{ ...td, fontWeight: 700, color: SEVERITY[s.riskTier as RiskSeverity]?.color ?? theme.color.text }}>{s.riskTier[0].toUpperCase() + s.riskTier.slice(1)}</td>
                    <td style={{ ...td, textAlign: "center" }}>{s.overallScore != null ? `${s.overallScore}%` : "—"}</td>
                    <td style={{ ...td, color: overdueSupplier(s.nextReviewDate) ? theme.color.dangerText : theme.color.text }}>{s.nextReviewDate ? fmtShort(s.nextReviewDate) : "Not scheduled"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Operator of essential services — only when an OES regime actually reaches them. */}
      {m.oes && (() => {
        const o = m.oes;
        const problems = [
          o.submissions?.overdue ? `${plural(o.submissions.overdue, "regulatory notification")} past a statutory deadline` : null,
          o.aiSystems?.prohibited ? plural(o.aiSystems.prohibited, "prohibited AI practice") : null,
          o.segmentation?.safetyExposed ? `${plural(o.segmentation.safetyExposed, "path")} reaching a safety zone` : null,
          o.segmentation?.high ? plural(o.segmentation.high, "high-severity segmentation finding") : null,
        ].filter(Boolean) as string[];
        return (
          <div style={card}>
            <div style={sectionTitle}>Operator of essential services</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 10 }}>{o.basis}</div>
            <div style={{
              padding: "10px 12px", borderRadius: 3, marginBottom: 12,
              background: problems.length ? "var(--c-panelAlt)" : "var(--c-panel)",
              borderLeft: `3px solid ${problems.length ? theme.color.warning : theme.color.success}`,
            }}>
              <strong style={{ color: theme.color.text, fontSize: 12 }}>{problems.length ? "Outstanding: " : "Position: "}</strong>
              <span style={{ fontSize: 12, color: theme.color.subtle }}>
                {problems.length
                  ? problems.join("; ")
                  : "nothing outstanding — no overdue statutory notification, no prohibited AI practice and no unresolved segmentation finding."}
              </span>
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
              {o.digest.slice(1).map((line, i) => <li key={i}>{line}</li>)}
            </ul>
            {o.supervision.length > 0 && (
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 10 }}>
                Supervised by {o.supervision.map((s) => `${s.authority} (${s.frameworkId})`).join(", ")}.
              </div>
            )}
          </div>
        );
      })()}

      {/* Operational resilience */}
      {m.resilience && (() => {
        const r = m.resilience;
        const n = r.incidents.notifications;
        const rows: Array<[string, string]> = [
          ["Testing and exercises", `${r.exercises.completedLast12Months} completed in 12 months${r.exercises.byKind.length ? ` (${r.exercises.byKind.map((k) => `${k.label} ${k.count}`).join(", ")})` : ""}; ${r.exercises.withSuppliers} with suppliers; ${r.exercises.overdue} overdue; last response exercise ${r.exercises.lastResponseExercise ? fmtShort(r.exercises.lastResponseExercise) : "none"}; last recovery test ${r.exercises.lastRecoveryTest ? fmtShort(r.exercises.lastRecoveryTest) : "none"}`],
          ["Training and awareness", `${r.training.covered} of ${r.training.members} members with in-date awareness training${r.training.coveragePct != null ? ` (${r.training.coveragePct}%)` : ""}; ${r.training.expiringWithin30Days} expiring within 30 days; phishing simulations ${r.training.phishing.simulations}${r.training.phishing.clickRate != null ? `, click rate ${r.training.phishing.clickRate}%` : ""}`],
          ["Assets", `${r.assets.total} recorded; ${r.assets.critical} critical, ${r.assets.essentialFunctions} supporting essential functions; ${r.assets.withoutContingencyPlan} critical without a contingency plan; ${r.assets.untestedContingency} untested in 12 months`],
          ["Cyber insurance", r.insurance ? `${r.insurance.insurer}, expires ${fmtShort(r.insurance.expiresAt)}${r.insurance.notificationWindowHours ? `, notify within ${r.insurance.notificationWindowHours} h` : ""}${r.insurance.retainer ? ", incident-response retainer" : ""}` : "None recorded"],
          ["Threat advisories", `${r.advisories.highOrCriticalLast30Days} high / critical from ${r.advisories.certSources.join(", ") || "no configured CERT"} in 30 days; ${r.advisories.triaged} triaged${r.advisories.triagePct != null ? ` (${r.advisories.triagePct}%)` : ""}`],
          ...(r.exposure ? [["Vulnerability exposure", r.exposure.advisories === 0
            ? `No advisory currently reaches an asset on the register${r.exposure.lastRunAt ? ` (evaluated ${fmtShort(r.exposure.lastRunAt)})` : ""}`
            : `${r.exposure.advisories} advisor${r.exposure.advisories === 1 ? "y" : "ies"} reach ${r.exposure.assets} asset${r.exposure.assets === 1 ? "" : "s"} (${r.exposure.confirmed} confirmed, ${r.exposure.likely} likely; ${r.exposure.importantAssets} critical / essential); ${r.exposure.awaitingAction} awaiting action; ${r.exposure.risksOpen} automatic register entr${r.exposure.risksOpen === 1 ? "y" : "ies"} open${r.exposure.top.length ? ` — ${r.exposure.top.slice(0, 3).map((t) => `${t.reference} (${t.assets})`).join(", ")}` : ""}`] as [string, string]] : []),
          ["Supplier assurance", `${r.suppliers.prospective} awaiting procurement approval; ${plural(r.suppliers.certifications, "certification")} on file, ${r.suppliers.certificationsExpired} expired, ${r.suppliers.certificationsExpiring60Days} expiring within 60 days`],
        ];
        return (
          <div style={card}>
            <div style={sectionTitle}>Operational resilience</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.subtle, marginBottom: 8 }}>Cyber incidents in the last 12 months, by severity</div>
            <CountTiles counts={r.incidents.bySeverity} />
            <div style={{ fontSize: 12, color: theme.color.subtle, margin: "10px 0 14px" }}>
              <strong style={{ color: theme.color.text }}>{plural(r.incidents.last12Months, "incident")} in 12 months, {r.incidents.open} open</strong> · {r.incidents.significant} assessed significant · {r.incidents.personalData} involving personal data · median {r.incidents.medianHoursToContain ?? "n/a"} h to contain · notifications: {n.sentOnTime} on time, {n.late} late, {n.overdue} overdue of {n.required} required
            </div>
            {r.incidents.recent.length > 0 && (
              <div style={{ overflowX: "auto", marginBottom: 14 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead><tr><th style={th}>Incident</th><th style={th}>Category</th><th style={th}>Severity</th><th style={th}>Status</th><th style={th}>Detected</th></tr></thead>
                  <tbody>
                    {r.incidents.recent.map((i) => (
                      <tr key={i.reference + i.detectedAt}>
                        <td style={td}><span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.muted }}>{i.reference}</span> {i.title}</td>
                        <td style={td}>{i.category}</td>
                        <td style={{ ...td, fontWeight: 700, color: SEVERITY[i.severity as RiskSeverity]?.color ?? theme.color.text }}>{i.severity[0].toUpperCase() + i.severity.slice(1)}</td>
                        <td style={td}>{i.status}</td>
                        <td style={td}>{fmtShort(i.detectedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                {rows.map(([a, b]) => (
                  <tr key={a}><td style={{ ...td, fontWeight: 600, width: "22%", verticalAlign: "top" }}>{a}</td><td style={td}>{b}</td></tr>
                ))}
              </tbody>
            </table>
            {/* Asset inventory — how the register is maintained (absent on reports stored before asset discovery) */}
            {r.assets.discovered != null && (() => {
              const inv = r.assets;
              const sources = inv.bySource ?? [];
              const manual = inv.manual ?? 0;
              const nc = inv.endpointsNonCompliant;
              const signals = (inv.ownerInactive ?? 0) > 0 || (nc?.important ?? 0) > 0;
              const legend = [...sources.map((s) => ({ label: `${s.label} ${s.count}`, color: sourceColor(s.source) })), { label: `Entered by members ${manual}`, color: sourceColor("manual") }];
              return (
                <>
                  <div style={subTitle}>Asset inventory — how the register is maintained</div>
                  <BarRow label="Connected-source coverage" sub={`${inv.discovered} of ${inv.total} assets discovered · ${manual} entered by members`}
                    segments={[...sources.map((s) => ({ value: s.count, color: sourceColor(s.source) })), { value: manual, color: sourceColor("manual") }]}
                    total={inv.total} value={inv.coveragePct != null ? `${inv.coveragePct}%` : "n/a"} />
                  <div style={{ display: "flex", gap: 14, marginTop: 10, flexWrap: "wrap" }}>
                    {legend.map(({ color, label }) => (
                      <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: theme.color.subtle }}>
                        <span style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />{label}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 10, lineHeight: 1.6 }}>
                    Last discovery pass {inv.lastDiscoveryAt ? fmtShort(inv.lastDiscoveryAt) : "never run"} · {plural(inv.unreviewed ?? 0, "discovered asset")} awaiting review · {inv.retiredLast90Days ?? 0} retired in the last 90 days · <strong style={{ color: signals ? theme.color.warningText : theme.color.subtle, fontWeight: signals ? 700 : 400 }}>{plural(inv.ownerInactive ?? 0, "important asset")} whose owner is no longer active · {plural(nc?.total ?? 0, "endpoint")} non-compliant in Intune ({nc?.important ?? 0} important)</strong>
                  </div>
                </>
              );
            })()}
            {r.attention.top.length > 0 && (
              <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 10, lineHeight: 1.6 }}>
                <strong style={{ color: theme.color.text }}>Outstanding resilience actions: {r.attention.high} high, {r.attention.medium} medium, {r.attention.low} low</strong> — {r.attention.top.map((a) => a.title).join("; ")}.
              </div>
            )}
          </div>
        );
      })()}

      {/* Corrective actions and improvement (ISO 27001 9.3, 10.2) — absent on reports stored before the section existed */}
      {m.improvement && (() => {
        const im = m.improvement;
        const e = im.effectiveness;
        return (
          <div style={card}>
            <div style={sectionTitle}>Corrective actions and improvement</div>
            {im.total === 0 ? (
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                No corrective action has been recorded yet. Nonconformities from audits, incidents, exercises and the Statement of Applicability are raised here, so an empty register usually means they are not being captured.
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
                  <Tile value={String(im.open)} caption="Open" />
                  <Tile value={String(im.overdue)} caption="Overdue" color={im.overdue > 0 ? RAG.gap : theme.color.faint} />
                  <Tile value={String(im.awaitingVerification)} caption="Awaiting verification" color={im.awaitingVerification > 0 ? RAG.partial : theme.color.faint} />
                  <Tile value={String(im.verifiedLast12Months)} caption="Verified in 12 months" color={im.verifiedLast12Months > 0 ? RAG.compliant : theme.color.faint} />
                </div>
                <div style={{ fontSize: 12, color: theme.color.subtle, margin: "10px 0 14px", lineHeight: 1.6 }}>
                  {im.blocked} blocked · {im.unowned} without an owner{im.medianDaysToVerify != null ? ` · median ${im.medianDaysToVerify} days from start to verification` : ""} ·{" "}
                  <strong style={{ color: e.overdue > 0 || e.notEffective > 0 ? theme.color.warningText : theme.color.subtle, fontWeight: e.overdue > 0 || e.notEffective > 0 ? 700 : 400 }}>
                    effectiveness reviews: {e.reviewed} done ({e.effective} effective, {e.notEffective} not effective), {e.due} due, {e.overdue} overdue
                  </strong>
                </div>
                {im.bySource.length > 0 && (
                  <div style={{ overflowX: "auto", marginBottom: 14 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr><th style={th}>Raised from</th><th style={th}>Open</th><th style={th}>Overdue</th><th style={th}>Verified</th></tr></thead>
                      <tbody>
                        {im.bySource.map((s) => (
                          <tr key={s.source}>
                            <td style={td}>{s.label}</td>
                            <td style={td}>{s.open}</td>
                            <td style={{ ...td, fontWeight: s.overdue > 0 ? 700 : 400, color: s.overdue > 0 ? RAG.gap : theme.color.text }}>{s.overdue}</td>
                            <td style={td}>{s.verified}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {im.programmes.length > 0 && (
                  <>
                    <div style={subTitle}>Improvement programmes — verified against time elapsed</div>
                    {im.programmes.map((p) => (
                      <BarRow key={p.name} label={p.name}
                        sub={`${p.status}${p.elapsedPct != null ? ` · ${p.elapsedPct}% of time gone` : ""}${p.behindSchedule ? " · behind schedule" : ""} · ${p.open} open, ${p.overdue} overdue`}
                        segments={[{ value: p.completionPct, color: p.behindSchedule ? RAG.gap : RAG.compliant }]} total={100} value={`${p.completionPct}%`} />
                    ))}
                  </>
                )}
                {im.topOverdue.length > 0 && (
                  <>
                    <div style={subTitle}>Longest overdue</div>
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr><th style={th}>Action</th><th style={th}>Raised from</th><th style={th}>Owner</th><th style={th}>Late</th></tr></thead>
                        <tbody>
                          {im.topOverdue.map((a) => (
                            <tr key={a.reference + a.title}>
                              <td style={td}><span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.muted }}>{a.reference}</span> {a.title}</td>
                              <td style={td}>{a.source}</td>
                              <td style={td}>{a.owner ?? "—"}</td>
                              <td style={{ ...td, fontWeight: 700, color: RAG.gap }}>{a.daysOverdue} d</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* Certification and evidence */}
      <div style={card}>
        <div style={sectionTitle}>Certification and evidence</div>
        {m.certification.engagements.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Standard</th><th style={th}>Certification body</th><th style={th}>Engaged since</th><th style={th}>Engaged until</th></tr></thead>
              <tbody>
                {m.certification.engagements.map((e) => (
                  <tr key={e.frameworkId}><td style={td}>{e.label}</td><td style={td}>{e.bureau}</td><td style={td}>{fmtShort(e.selectedAt)}</td><td style={td}>{e.lockedUntil ? fmtShort(e.lockedUntil) : "Open"}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: theme.color.subtle }}>No certification body is engaged yet. Engaging a bureau per standard fixes the audit calendar and unlocks auditor access to evidence.</div>
        )}
        <div style={{ fontSize: 12, color: theme.color.text, marginTop: 10, lineHeight: 1.6 }}>
          {m.certification.upcomingAudits.length > 0 && <><strong>Upcoming audits:</strong> {m.certification.upcomingAudits.map((a) => `${a.auditType} with ${a.bureau} on ${fmtShort(a.scheduledDate)}`).join("; ")}. </>}
          {m.certification.lastCompletion && <><strong>Last audit sign-off:</strong> {m.certification.lastCompletion.bureau}, {fmtShort(m.certification.lastCompletion.completedAt)}{m.certification.lastCompletion.overallScore != null ? ` at ${m.certification.lastCompletion.overallScore}%` : ""}. </>}
          <strong>Open auditor findings:</strong> {m.certification.openFindings}.
          {m.certification.upcomingEvents.length > 0 && <div style={{ color: theme.color.subtle }}><strong style={{ color: theme.color.text }}>Next 90 days:</strong> {m.certification.upcomingEvents.map((e) => `${e.title} (${fmtShort(e.eventDate)})`).join("; ")}.</div>}
        </div>
        <div style={subTitle}>Evidence coverage — applicable controls with evidence attached</div>
        {m.evidence.byFramework.map((f) => (
          f.applicable === 0
            // Nothing in scope to evidence (a law with no applicable duty, a maturity-only standard): n/a, never "0 of 0 — 0%".
            ? <BarRow key={f.frameworkId} label={f.label} sub="No applicable controls" segments={[]} total={1} value="n/a" />
            : <BarRow key={f.frameworkId} label={f.label} sub={`${f.withEvidence} of ${f.applicable} applicable controls`} segments={[{ value: f.withEvidence, color: "var(--c-accent)" }]} total={f.applicable} value={`${f.pct}%`} ruler />
        ))}
        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 8 }}>
          {plural(m.evidence.documents, "evidence document")} in the repository · {plural(m.evidence.policiesGenerated, "policy document")} generated
          {m.evidence.policiesStale > 0 && <strong style={{ color: theme.color.warningText }}> · {m.evidence.policiesStale} stale since the organisation profile changed</strong>}
        </div>
      </div>

      {/* Priority gaps */}
      {m.topGaps.length > 0 && (
        <div style={card}>
          <div style={sectionTitle}>Priority gaps</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={{ ...th, textAlign: "center" }}>#</th><th style={th}>Control</th><th style={th}>Standard</th><th style={th}>Status</th><th style={th}>Recommended action</th></tr></thead>
              <tbody>
                {m.topGaps.map((g, i) => (
                  <tr key={`${g.frameworkId}-${g.controlId}`}>
                    <td style={{ ...td, textAlign: "center", color: theme.color.subtle }}>{i + 1}</td>
                    <td style={td}><span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.subtle, marginRight: 6 }}>{displayControlRef(g.controlId)}</span>{g.name}{g.linkedOpenRisks > 0 && <span style={{ color: theme.color.subtle }}> ({g.linkedOpenRisks} open risk{g.linkedOpenRisks === 1 ? "" : "s"})</span>}</td>
                    <td style={td}>{g.frameworkLabel}</td>
                    <td style={td}><StatusTag tone={g.status === "GAP" ? "gap" : "partial"}>{g.status === "GAP" ? "Gap" : "Partial"}</StatusTag></td>
                    <td style={td}>{g.recommendation ?? g.gap ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
